/** Homepage module — "Help bring them home": recent missing + found-safe cards
 *  plus the three freshest headlines from the disaster wire. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { MapPin, Search, Plus, MessageCircleHeart, Radio, ExternalLink } from "lucide-react";
import StatusBadge from "@/components/site/StatusBadge";
import { fileUrl } from "@/lib/files";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MissingBand() {
  const [cases, setCases] = useState([]);
  const [wire, setWire] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/v1/missing`, { params: { limit: 4 } })
      .then(({ data }) => setCases(data.cases || []))
      .catch(() => setCases([]));
    axios
      .get(`${API}/v1/news`, { params: { limit: 3 } })
      .then(({ data }) => setWire(data.articles || []))
      .catch(() => setWire([]));
  }, []);

  return (
    <section className="border-y border-[color:var(--sepia)]/25" data-testid="home-missing-band">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-16 md:py-20">
        <div className="grid grid-cols-12 gap-8 items-start">
          <div className="col-span-12 lg:col-span-5">
            <div className="overline text-[color:var(--rust)] mb-4">§ Missing &amp; Found</div>
            <h2 className="heritage-title leading-[0.95]" style={{ fontSize: "clamp(36px, 5vw, 84px)" }}>
              Help bring them <span className="font-italic-serif italic text-[color:var(--rust)]">home.</span>
            </h2>
            <p className="mt-5 max-w-xl text-[16px] leading-[1.65] text-[color:var(--ink-soft)]">
              Missing people around the world need to be remembered, searched for and found.
              A share can carry a face to the one person who has seen it.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/missing" className="btn-wood" data-testid="home-missing-search">
                <Search size={14} /> Search missing people
              </Link>
              <Link to="/missing/report" className="btn-rust" data-testid="home-missing-report">
                <Plus size={14} /> Report a missing person
              </Link>
              <Link to="/disasters" className="btn-ghost" data-testid="home-disasters-link">
                Disasters →
              </Link>
            </div>
          </div>
          <div className="col-span-12 lg:col-span-4">
            {cases.length > 0 ? (
              <div className="grid grid-cols-1 min-[420px]:grid-cols-2 gap-4" data-testid="home-missing-cards">
                {cases.map((c) => (
                  <Link
                    key={c.case_id}
                    to={`/missing/case/${c.case_id}`}
                    data-testid={`home-missing-card-${c.case_id}`}
                    className="group border border-[color:var(--sepia)]/25 bg-[color:var(--parchment)] p-4 block"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <StatusBadge status={c.status} />
                    </div>
                    <div className="mt-3 flex items-center gap-3">
                      <div className="w-12 h-14 shrink-0 overflow-hidden bg-[color:var(--parchment-2)] flex items-center justify-center">
                        {c.photo_path ? (
                          <img src={fileUrl(c.photo_path)} alt="" className="w-full h-full object-cover" style={{ filter: "sepia(15%)" }} />
                        ) : (
                          <span className="font-serif-display text-2xl text-[color:var(--sepia)]/50">{c.name?.[0]}</span>
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="font-serif-display leading-tight truncate group-hover:text-[color:var(--rust)] transition-colors">{c.name}</div>
                        <div className="mt-0.5 flex items-center gap-1 text-[11px] text-[color:var(--ink-soft)]">
                          <MapPin size={9} /> {[c.loc?.city, c.loc?.country].filter(Boolean).join(", ")}
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
                <Link
                  to="/missing"
                  data-testid="home-missing-more"
                  className="col-span-2 text-center text-xs link-underline text-[color:var(--sepia)] pt-1 inline-flex items-center justify-center gap-1 whitespace-nowrap"
                >
                  <MessageCircleHeart size={12} /> <span>Seen someone? Say so — privately</span>
                </Link>
              </div>
            ) : (
              <div className="border border-dashed border-[color:var(--sepia)]/40 p-8 text-center" data-testid="home-missing-empty">
                <p className="font-italic-serif italic text-lg text-[color:var(--ink-soft)]">
                  The ledger is open for families who need it.
                </p>
              </div>
            )}
          </div>

          {/* The Wire — three freshest headlines */}
          {wire.length > 0 && (
            <div className="col-span-12 lg:col-span-3 border-l-0 lg:border-l border-[color:var(--sepia)]/25 lg:pl-8" data-testid="home-wire">
              <div className="overline text-[color:var(--sepia)] mb-4 flex items-center gap-2">
                <Radio size={12} className="text-[color:var(--rust)]" /> The wire, hourly
              </div>
              <ol className="space-y-4">
                {wire.map((a, i) => (
                  <li key={a.id} data-testid={`home-wire-${i}`}>
                    <a
                      href={a.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-baseline gap-2"
                    >
                      <span className="font-serif-display text-[15px] leading-snug group-hover:text-[color:var(--rust)] transition-colors">
                        {a.title}
                      </span>
                      <ExternalLink size={10} className="shrink-0 text-[color:var(--sepia)]/50" />
                    </a>
                    <div className="mt-0.5 numeric-index text-[color:var(--ink-soft)]">{a.source}</div>
                  </li>
                ))}
              </ol>
              <Link to="/news" className="mt-5 inline-block text-xs link-underline text-[color:var(--rust)]" data-testid="home-wire-more">
                Open the full wire →
              </Link>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
